import React, {useLayoutEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from "react-intl";

import {styled} from "@mui/material/styles";

import Badge from "components/Badge";
import CustomTooltip from "components/CustomTooltip";
import ValidationErrorsTooltip from "components/Product/Table/ValidationErrorsTooltip";
import {hasValidationErrors} from "components/Product/Table/validationFieldLabel";
import {ReactComponent as AlertTriangleIcon} from "assets/img/alertTriangle.svg";
import {ReactComponent as EyeIcon} from "assets/img/eye.svg";
import {black500, greyishBlue400} from "assets/jss/main";

// The badges wrap on the column width, so the collapse threshold is a number of lines,
// not of badges: past this many lines the list is cut behind the show more button, and
// the cut point is re-measured whenever the column is resized.
const DEFAULT_COLLAPSED_LINES_COUNT = 4;

ListingStockCell.propTypes = {
    // Actif / Inactif pill — omit to hide it
    active: PropTypes.bool,
    // validation OK / KO pill — omit to hide it
    validated: PropTypes.bool,
    // KO pill tooltip content, in either of the two shapes the row can come with:
    // the {path, message} pairs of the Elasticsearch document, or the flattened
    // string the REST collection publishes
    validationErrors: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.shape({
            path: PropTypes.string,
            message: PropTypes.string,
        })),
    ]),
    // push-error pill — omit to hide it
    hasPushError: PropTypes.bool,
    // "Voir sur le site" target — omit to hide the link
    siteUrl: PropTypes.string,
    // one badge per variation, colored by stock level
    stocks: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
        stock: PropTypes.number,
    })),
};

// The only flexible column: it absorbs whatever the fixed columns leave and keeps growing
// on wider screens, with 235px as its floor.
//
// The floor is a product decision, not a DS value — the design declares the column
// flex-1 with min-w-px, i.e. free to shrink. 235px keeps the whole table just under the
// 1080px reference row: 644 of fixed columns + 160 of gutters + 235 + 40 of row padding
// = 1079. Its tags wrap, so the column degrades by stacking rather than overflowing.
const Container = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '235px'
});

const TagsRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    width: '100%'
});

const Pills = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    flex: 1,
    minWidth: 0
});

const SiteLink = styled('a')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: black500,
    fontSize: '12px',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
        textDecoration: 'underline'
    },
    'svg': {
        width: '16px',
        height: '16px',
        flexShrink: 0
    }
});

const StocksContainer = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '4px'
});

const StocksWrapper = styled('div')({
    position: 'relative'
});

// Off-screen copy of the full badge list: the visible list is cut from what this one
// measures, so it must share the container's width and wrapping rules.
const HiddenStocks = styled(StocksContainer)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    visibility: 'hidden',
    pointerEvents: 'none'
});

// Badge is a plain function component: it neither forwards refs nor spreads
// the tooltip's event handlers, so the tooltip anchors on this wrapper
const TooltipAnchor = styled('span')({
    display: 'inline-flex'
});

const PUSH_ERROR_BADGE_THEME = {padding: {default: '3px'}};

const PushErrorIcon = styled(AlertTriangleIcon)({
    display: 'block'
});

// Sits on its own line under the badges, spaced like a badge line
const ShowMore = styled('button')({
    display: 'block',
    marginTop: '4px',
    padding: 'unset',
    border: 'none',
    backgroundColor: 'transparent',
    color: greyishBlue400,
    fontSize: '12px',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
    cursor: 'pointer'
});

// A variation name can be arbitrarily long and the badge does not wrap, so one badge
// could spill past the column; this clamps the badge to the column width and lets the
// label ellipsise. Badge forwards neither className nor refs, hence a wrapper reaching
// into its container div and label span.
const BadgeClamp = styled('span')({
    display: 'inline-flex',
    maxWidth: '100%',
    '& > div': {
        maxWidth: '100%'
    },
    // the label span becomes the flex line "name · stock" where only the name shrinks
    '& > div > span': {
        display: 'flex',
        minWidth: 0,
        gap: '3px'
    }
});

const ClampedLabel = styled('span')({
    overflow: 'hidden',
    textOverflow: 'ellipsis'
});

const stockBadgeColor = (stock) => {
    if (stock > 5) {
        return 'online';
    }

    return stock > 0 ? 'warning' : 'error';
};

function StockBadge({label, stock}) {
    return (
        <BadgeClamp>
            <Badge color={stockBadgeColor(stock)} border>
                {label
                    ? <><ClampedLabel>{label}</ClampedLabel><span>{`· ${stock}`}</span></>
                    : `${stock}`}
            </Badge>
        </BadgeClamp>
    );
}

// The Actif / OK-KO / push-error pill row, shared with the mobile article cell.
export function ListingStatusPills({active, validated, validationErrors, hasPushError}) {
    const intl = useIntl();

    const validationPill = validated
        ? <Badge rounded color="online" border>OK</Badge>
        : <Badge rounded color="error" border>KO</Badge>;

    const tooltipContent = Array.isArray(validationErrors)
        ? <ValidationErrorsTooltip errors={validationErrors}/>
        : validationErrors;

    return (
        <Pills>
            {undefined !== active && (
                <Badge rounded color={active ? "online" : "error"} border indicator="circle">
                    <FormattedMessage id={active ? "retailer.product.list.label.active" : "retailer.product.list.label.inactive"}/>
                </Badge>
            )}
            {undefined !== validated && (
                !validated && hasValidationErrors(validationErrors)
                    ? (
                        <CustomTooltip title={tooltipContent}>
                            <TooltipAnchor>{validationPill}</TooltipAnchor>
                        </CustomTooltip>
                    )
                    : validationPill
            )}
            {true === hasPushError && (
                <CustomTooltip title={intl.formatMessage({id: "retailer.product.list.label.errorOnPush"})}>
                    <TooltipAnchor>
                        <Badge rounded color="error" border themeExtension={PUSH_ERROR_BADGE_THEME}>
                            <PushErrorIcon/>
                        </Badge>
                    </TooltipAnchor>
                </CustomTooltip>
            )}
        </Pills>
    );
}

// The per-variation stock badge list with its measured collapse behind the show
// more button, shared with the mobile article cell.
export function ListingStockBadges({stocks = [], collapsedLinesCount = DEFAULT_COLLAPSED_LINES_COUNT}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(stocks.length);
    const measureRef = useRef(null);

    useLayoutEffect(() => {
        const node = measureRef.current;

        if (!node) {
            return;
        }

        const measure = () => {
            const children = node.children;

            const lineTops = [];
            for (let index = 0; index < children.length; index++) {
                if (!lineTops.includes(children[index].offsetTop)) {
                    lineTops.push(children[index].offsetTop);
                }
            }

            if (lineTops.length <= collapsedLinesCount) {
                setVisibleCount(children.length);

                return;
            }

            // the show more button lands on its own line below, so the cut is simply the end of the last kept line
            const lastLineTop = lineTops[collapsedLinesCount - 1];

            let count = children.length;
            while (count > 0 && children[count - 1].offsetTop > lastLineTop) {
                count--;
            }

            setVisibleCount(count);
        };

        measure();

        // jsdom has no ResizeObserver; there the initial measure (all offsets at 0) shows everything
        if ('undefined' === typeof ResizeObserver) {
            return;
        }

        const observer = new ResizeObserver(measure);
        observer.observe(node);

        return () => observer.disconnect();
    }, [stocks, collapsedLinesCount]);

    const hasOverflow = visibleCount < stocks.length;
    const visibleStocks = isExpanded ? stocks : stocks.slice(0, visibleCount);

    if (0 === stocks.length) {
        return null;
    }

    return (
        <StocksWrapper>
            <HiddenStocks ref={measureRef} aria-hidden="true">
                {stocks.map((entry) => (
                    <StockBadge key={entry.id} label={entry.label} stock={entry.stock}/>
                ))}
            </HiddenStocks>
            <StocksContainer>
                {visibleStocks.map((entry) => (
                    <StockBadge key={entry.id} label={entry.label} stock={entry.stock}/>
                ))}
            </StocksContainer>
            {hasOverflow && (
                <ShowMore
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                >
                    <FormattedMessage id={isExpanded ? "retailer.product.list.stock.showLess" : "retailer.product.list.stock.showMore"}/>
                </ShowMore>
            )}
        </StocksWrapper>
    );
}

function ListingStockCell({active, validated, validationErrors, hasPushError, siteUrl, stocks = []}) {
    return (
        <Container>
            <TagsRow>
                <ListingStatusPills
                    active={active}
                    validated={validated}
                    validationErrors={validationErrors}
                    hasPushError={hasPushError}
                />
                {siteUrl && (
                    <SiteLink
                        href={siteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <FormattedMessage id={"retailer.product.list.stock.seeOnSite"}/>
                        <EyeIcon/>
                    </SiteLink>
                )}
            </TagsRow>
            <ListingStockBadges stocks={stocks}/>
        </Container>
    );
}

export default ListingStockCell;
